/**
 * ContractPriceListTab — Szerződéses árjegyzék tab (Q-IMP-3)
 *
 * PartnerDetailPage-re kerülő tab:
 * - Excel feltöltés gomb
 * - Árjegyzék táblázat (termék, vevői cikkszám, szerződéses ár, érvényesség)
 * - Import eredmény összesítő
 */

import { ApiError } from '@/api/client';
import {
  downloadExport,
  downloadTemplate,
  getContractPrices,
  importContractPricesXlsx,
  previewContractPriceImport,
  type ContractPriceEntry,
  type ImportDiffItem,
  type ImportPreviewResult,
  type ImportResult,
} from '@/api/contract-prices';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { AlertTriangle, CheckCircle, Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ContractPriceListTabProps {
  partnerId: string;
}

export function ContractPriceListTab({ partnerId }: ContractPriceListTabProps) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<ContractPriceEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getContractPrices(partnerId);
      setEntries(response.data);
    } catch {
      // silently handle — empty state will show
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsImporting(true);
      setImportResult(null);
      setImportError(null);
      setPreview(null);
      setPendingFile(file);

      try {
        // Step 1: Preview diff (AC-4)
        const previewResponse = await previewContractPriceImport(partnerId, file);
        setPreview(previewResponse.data);
        // Don't auto-import — user reviews preview first
      } catch (err) {
        setImportError(err instanceof Error ? err.message : t('contractPriceList.importError'));
        setPendingFile(null);
      } finally {
        setIsImporting(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [partnerId, t]
  );

  const confirmImport = useCallback(
    async (force = false) => {
      if (!pendingFile) return;

      setIsImporting(true);
      setImportError(null);
      setShowForceConfirm(false);

      try {
        const response = await importContractPricesXlsx(partnerId, pendingFile, force);
        setImportResult(response.data);
        setPreview(null);
        setPendingFile(null);

        if (response.data.successful > 0) {
          void loadEntries();
        }
      } catch (err) {
        // AC-3: Annual limit → show force confirm dialog
        if (err instanceof ApiError && err.statusCode === 409) {
          setShowForceConfirm(true);
        } else {
          setImportError(err instanceof Error ? err.message : t('contractPriceList.importError'));
          setPendingFile(null);
          setPreview(null);
          // #10 FIX: Reset file input so re-selecting same file triggers onChange
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      } finally {
        setIsImporting(false);
      }
    },
    [partnerId, pendingFile, loadEntries, t]
  );

  const cancelImport = useCallback(() => {
    setPreview(null);
    setPendingFile(null);
    setShowForceConfirm(false);
    setImportError(null);
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const getStatusBadge = (entry: ContractPriceEntry) => {
    if (!entry.isOrderable) {
      return (
        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {t('contractPriceList.notOrderable')}
        </span>
      );
    }

    if (entry.isExpired) {
      return (
        <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {t('contractPriceList.expired')}
        </span>
      );
    }

    const now = new Date();
    const validFrom = new Date(entry.validFrom);
    if (validFrom > now) {
      return (
        <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
          {t('contractPriceList.notYetValid')}
        </span>
      );
    }

    return (
      <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 dark:bg-green-900/20 dark:text-green-300">
        {t('contractPriceList.active')}
      </span>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
          <p className="mt-2 text-gray-500 dark:text-[#ccfbf1]">{t('common.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Import Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('contractPriceList.title')}</CardTitle>
            <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
              {t('contractPriceList.orderableOnly')}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Template download (AC-5) — fetch-based with auth */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void downloadTemplate(partnerId)}
              data-testid="contract-price-template-btn"
            >
              <Download className="mr-1 h-4 w-4" />
              {t('contractPriceList.templateDownload')}
            </Button>
            {/* Export (AC-6) — fetch-based with auth */}
            {entries.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void downloadExport(partnerId)}
                data-testid="contract-price-export-btn"
              >
                <Download className="mr-1 h-4 w-4" />
                {t('contractPriceList.export')}
              </Button>
            )}
            {/* Import upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="contract-price-file-input"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
              data-testid="contract-price-import-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              {isImporting ? t('common.loading') : t('contractPriceList.importButton')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* AC-3: Annual limit force confirm dialog */}
          {showForceConfirm && (
            <div
              className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20"
              data-testid="force-confirm-dialog"
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-yellow-800 dark:text-yellow-200">
                  {t('contractPriceList.annualLimitWarning')}
                </span>
              </div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                {t('contractPriceList.annualLimitDescription')}
              </p>
              <div className="flex gap-2">
                <Button variant="destructive" size="sm" onClick={() => void confirmImport(true)}>
                  {t('contractPriceList.forceImport')}
                </Button>
                <Button variant="outline" size="sm" onClick={cancelImport}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          )}

          {/* AC-4: Import Preview/Diff */}
          {preview && !importResult && (
            <div
              className="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20"
              data-testid="import-preview"
            >
              <h4 className="mb-2 font-medium text-blue-800 dark:text-blue-200">
                {t('contractPriceList.importPreview')}
              </h4>
              <div className="mb-3 flex gap-4 text-sm">
                {preview.added > 0 && (
                  <span className="text-green-600">
                    {t('contractPriceList.previewNew', { count: preview.added.toString() })}
                  </span>
                )}
                {preview.changed > 0 && (
                  <span className="text-yellow-600">
                    {t('contractPriceList.previewChanged', { count: preview.changed.toString() })}
                  </span>
                )}
                {preview.removed > 0 && (
                  <span className="text-red-600">
                    {t('contractPriceList.previewRemoved', { count: preview.removed.toString() })}
                  </span>
                )}
                {preview.unchanged > 0 && (
                  <span className="text-muted-foreground">
                    {t('contractPriceList.previewUnchanged', {
                      count: preview.unchanged.toString(),
                    })}
                  </span>
                )}
              </div>
              {/* Diff table */}
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="px-2 py-1 text-left">{t('contractPriceList.customerSku')}</th>
                      <th className="px-2 py-1 text-left">{t('contractPriceList.product')}</th>
                      <th className="px-2 py-1 text-right">{t('contractPriceList.oldPrice')}</th>
                      <th className="px-2 py-1 text-right">{t('contractPriceList.newPrice')}</th>
                      <th className="px-2 py-1 text-center">{t('contractPriceList.change')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items
                      .filter(i => i.diffStatus !== 'UNCHANGED')
                      .map(item => (
                        <tr key={item.customerSku} className={getDiffRowClass(item.diffStatus)}>
                          <td className="px-2 py-1 font-mono">{item.customerSku}</td>
                          <td className="px-2 py-1">{item.customerName ?? item.internalSku}</td>
                          <td className="px-2 py-1 text-right">
                            {item.oldPrice !== null ? formatPrice(item.oldPrice) : '-'}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {item.diffStatus !== 'REMOVED' ? formatPrice(item.newPrice) : '-'}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={getDiffBadgeClass(item.diffStatus)}>
                              {t(`contractPriceList.diff.${item.diffStatus}`)}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => void confirmImport()}>
                  {t('contractPriceList.confirmImport')}
                </Button>
                <Button variant="outline" size="sm" onClick={cancelImport}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div
              className={`mb-4 rounded-lg border p-4 ${
                importResult.failed > 0
                  ? 'border-yellow-300 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
                  : 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
              }`}
              data-testid="import-result"
            >
              <div className="flex items-center gap-2">
                {importResult.failed > 0 ? (
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
                <span className="font-medium">
                  {importResult.failed > 0
                    ? t('contractPriceList.importPartial', {
                        successful: importResult.successful.toString(),
                        failed: importResult.failed.toString(),
                      })
                    : t('contractPriceList.importSuccess', {
                        successful: importResult.successful.toString(),
                        total: importResult.total.toString(),
                      })}
                </span>
              </div>
              {importResult.rows.some(r => r.status === 'error') && (
                <div className="mt-2 space-y-1">
                  {importResult.rows
                    .filter(r => r.status === 'error')
                    .map(r => (
                      <p key={r.row} className="text-sm text-red-600">
                        {t('common.row')} {r.row}: {r.error}
                      </p>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Import Error */}
          {importError && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-red-700 dark:text-red-300">{importError}</span>
              </div>
            </div>
          )}

          {/* Price List Table */}
          {entries.length === 0 ? (
            <div className="py-8 text-center">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
              <p className="mt-2 text-gray-500 dark:text-[#ccfbf1]">
                {t('contractPriceList.noItems')}
              </p>
              <p className="mt-1 text-sm text-gray-400 dark:text-[#99f6e4]">
                {t('contractPriceList.dragDrop')}
              </p>
            </div>
          ) : (
            <>
              <p className="mb-3 text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('contractPriceList.itemCount', { count: entries.length.toString() })}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="contract-price-table">
                  <thead>
                    <tr className="border-b dark:border-[#2d7d8a]">
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('contractPriceList.customerSku')}
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('contractPriceList.product')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('contractPriceList.contractPrice')}
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('contractPriceList.validFrom')}
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('contractPriceList.validUntil')}
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('contractPriceList.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(entry => (
                      <tr key={entry.id} className="border-b last:border-0 dark:border-[#2d7d8a]">
                        <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">
                          {entry.customerSku}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">
                          {entry.customerName ?? entry.productId}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">
                          {formatPrice(entry.contractPrice)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-[#ccfbf1]">
                          {formatDate(entry.validFrom)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-[#ccfbf1]">
                          {formatDate(entry.validTo)}
                        </td>
                        <td className="px-3 py-2">{getStatusBadge(entry)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// Q-IMP-3: Diff display helpers
// ============================================

function getDiffRowClass(status: ImportDiffItem['diffStatus']): string {
  switch (status) {
    case 'NEW':
      return 'bg-green-50 dark:bg-green-900/10';
    case 'CHANGED':
      return 'bg-yellow-50 dark:bg-yellow-900/10';
    case 'REMOVED':
      return 'bg-red-50 dark:bg-red-900/10';
    default:
      return '';
  }
}

function getDiffBadgeClass(status: ImportDiffItem['diffStatus']): string {
  const base = 'rounded px-1.5 py-0.5 text-xs font-medium';
  switch (status) {
    case 'NEW':
      return `${base} bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300`;
    case 'CHANGED':
      return `${base} bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300`;
    case 'REMOVED':
      return `${base} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`;
    default:
      return `${base} bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300`;
  }
}
